// Who lands on the waitlist page, and how that is decided.
//
// A `network` user signed in SUCCESSFULLY. They are not an error and they are
// not a failed login — the exchange minted them a session and answered 200.
// What they have is an account with no access to THIS app yet, which is
// exactly the shape Ted described for the waitlist.
//
// What they must never do is reach Auger code that talks to Apollo. The
// `network` Hasura role can select two columns of its own `users` row and
// nothing else, so the boot query for email/name/admin/partner/record fails;
// on 2026-09-15 that produced "users_by_pk not found in type 'query_root'"
// and the app hung on "Signing you in" with nothing on screen to explain it.
// The hang is not a timeout — currentUser catches the Apollo error and
// logs it, so `userStore.email` is never set and `waitForLogin()` simply never
// resolves. That is the concrete failure this page fixes.
//
// The decision is made on ROLES, never by catching that Apollo error: the
// error string is not a contract, and by the time it is thrown the stores
// have already started booting. Roles are the audience roles Stytch holds
// (x-hasura-allowed-roles); a Heavybit staff role is what opens this app. The
// `x-hasura-user-class` claim is not read: it comes from users.class and goes
// away with the users table.

import { HASURA_CLAIMS, isHeavybitStaff, isRole } from "@auger/identity"

/** The fact the routing decision needs, from the exchange or the JWT. */
export interface IdentityPrincipal {
  roles: string[]
}

export const WAITLIST_PATH = "/identity/waitlist"

/**
 * A successful sign-in with no access to THIS app yet: a caller who holds no
 * Heavybit staff role. Staff are never sent here. A principal with no roles
 * (a token the page cannot read) routes normally, because Hasura decides
 * access anyway, and a staff member must never be stranded here. A Network
 * member always holds network_member: the exchange assigns it.
 */
export function isWaitlisted(principal: IdentityPrincipal | null | undefined): boolean {
  if (!principal) return false
  if (isHeavybitStaff(principal)) return false
  return principal.roles.length > 0
}

/**
 * WHICH message the waitlist page shows. Two people reach that page and they
 * are not in the same situation:
 *
 *   `waiting`          signed in, holds no audience role yet. Genuinely on the
 *                      list.
 *   `no_auger_access`  holds an audience role for another Heavybit product
 *                      (network_member: Crucible) and opened the wrong
 *                      application. Not on any list, and telling them they are
 *                      would be false.
 *
 * Returns null for anybody `isWaitlisted` would not route here, so the two can
 * never disagree about who belongs on the page. The gate stays the gate; this
 * only chooses words.
 */
export type WaitlistState = "waiting" | "no_auger_access"

export function waitlistState(
  principal: IdentityPrincipal | null | undefined
): WaitlistState | null {
  if (!principal || !isWaitlisted(principal)) return null
  return principal.roles.some(isRole) ? "no_auger_access" : "waiting"
}

// The claim name is the one in the Stytch templates: `x-hasura-allowed-roles`
// inside the Hasura namespace. Anything missing or the wrong type reads as
// "no roles", which routes normally — staff must never be sent here. Lenient on purpose, unlike @auger/identity's
// principalFromClaims: this routes the page, and Hasura decides access.

export function principalFromClaims(payload: Record<string, unknown> | null): IdentityPrincipal {
  const block = (payload?.[HASURA_CLAIMS] ?? {}) as Record<string, unknown>
  const roles = block["x-hasura-allowed-roles"]
  return {
    roles: Array.isArray(roles) ? roles.filter((r): r is string => typeof r === "string") : [],
  }
}
